import * as React from 'react'

import { Select as SelectPrimitive } from '@base-ui/react/select'

import { cn } from '../../../utils/cn'
import {
  triggerSizeVariants,
  type TriggerSize
} from '../../../utils/trigger-size-variants'
import { Icon } from '../../atoms/icon/icon'

const Select = SelectPrimitive.Root

export type SelectSize = TriggerSize

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot='select-group'
      className={cn('scroll-my-1 p-1', className)}
      {...props}
    />
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot='select-value'
      className={cn('flex flex-1 text-left', className)}
      {...props}
    />
  )
}

function SelectTrigger({
  className,
  size = 'md',
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: SelectSize
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot='select-trigger'
      data-size={size}
      className={cn(
        'border-outline bg-surface text-surface-title-50 flex w-fit select-none items-center justify-between gap-1.5 whitespace-nowrap rounded border outline-none transition-colors',
        'hover:border-primary-100 focus-visible:border-primary data-popup-open:border-primary',
        'disabled:bg-surface-200 disabled:text-surface-body disabled:hover:border-outline disabled:cursor-not-allowed',
        'aria-invalid:border-danger-icon',
        'data-placeholder:text-surface-body',
        '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5',
        triggerSizeVariants({ size }),
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <Icon
            name='keyboard_arrow_down'
            className='text-surface-title-50 in-data-popup-open:rotate-180 pointer-events-none text-[21px] transition-transform duration-150'
          />
        }
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  alignItemWithTrigger = false,
  separators = false,
  positionerClassName,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'
  > & {
    /** Draws a 1px outline-colored divider between selectable items */
    separators?: boolean
    /** Extra classes on the portal positioner, e.g. a higher z-index inside legacy modals */
    positionerClassName?: string
  }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className={cn('isolate z-50', positionerClassName)}
      >
        <SelectPrimitive.Popup
          data-slot='select-content'
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            'bg-surface text-surface-title-50 border-outline shadow-overlay max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) relative isolate z-50 min-w-36 overflow-y-auto overflow-x-hidden rounded border duration-100',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[align-trigger=true]:animate-none',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List
            className={cn(separators && 'divide-outline divide-y')}
          >
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot='select-label'
      className={cn(
        'text-surface-body text-caption-lg px-1.5 py-1 font-semibold',
        className
      )}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot='select-item'
      className={cn(
        'text-surface-body text-body-lg outline-hidden relative flex w-full cursor-default select-none items-center gap-1.5 px-4 py-3 font-normal leading-[1.5]',
        'focus:bg-surface-200 focus:text-surface-title-50 data-selected:text-surface-title-50',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className='flex flex-1 shrink-0 gap-2 whitespace-nowrap'>
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className='pointer-events-none absolute right-2 flex size-4 items-center justify-center' />
        }
      >
        <Icon name='check' className='pointer-events-none text-[16px]' />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot='select-separator'
      className={cn(
        'bg-outline pointer-events-none -mx-1 my-1 h-px',
        className
      )}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot='select-scroll-up-button'
      className={cn(
        'bg-surface text-surface-body top-0 z-10 flex w-full cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <Icon name='keyboard_arrow_up' className='text-[16px]' />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot='select-scroll-down-button'
      className={cn(
        'bg-surface text-surface-body bottom-0 z-10 flex w-full cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <Icon name='keyboard_arrow_down' className='text-[16px]' />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue
}
